import { useCallback, useRef } from 'react';
import { sanitizeFilename } from '../utils/fileUtils';
import { useMatrixClient } from '../context/MatrixContext';
import { useDownload } from '../context/DownloadContext';
import { useModal } from '../context/ModalContext';
import { useI18n } from '../context/I18nContext';
import logger from '../utils/logger';
import mediaCacheService from '../services/mediaCacheService';
import { DEFAULT_HOMESERVER } from '../config/homeserver';
import { timFetch } from '../utils/timUserAgent';

/**
 * Custom hook for handling file downloads from Matrix attachments with progress tracking
 * @returns {Object} Download functionality
 */
const useFileDownload = () => {
  const client = useMatrixClient();
  const { showError } = useModal();
  const { t } = useI18n();
  const { 
    startDownload, 
    updateProgress, 
    completeDownload, 
    escalateToModal, 
  } = useDownload();

  // Module-level cache health tracking (useRef to persist across renders)
  const cacheFailureCount = useRef(0);
  const CACHE_FAILURE_THRESHOLD = 5;
  const cacheDisabled = useRef(false);

  /**
   * Infers MIME type from URL/filename extension
   * Used as fallback when server doesn't provide Content-Type header
   * @param {string} url - URL or filename
   * @returns {string} MIME type or 'application/octet-stream' as fallback
   */
  const inferMimeType = (url) => {
    const mimeTypes = {
      '.pdf': 'application/pdf',
      '.png': 'image/png',
      '.jpg': 'image/jpeg',
      '.jpeg': 'image/jpeg',
      '.gif': 'image/gif',
      '.webp': 'image/webp',
      '.svg': 'image/svg+xml',
      '.bmp': 'image/bmp',
      '.ico': 'image/x-icon',
      '.mp4': 'video/mp4',
      '.webm': 'video/webm',
      '.mov': 'video/quicktime',
      '.avi': 'video/x-msvideo',
      '.mkv': 'video/x-matroska',
      '.mp3': 'audio/mpeg',
      '.ogg': 'audio/ogg',
      '.wav': 'audio/wav',
      '.m4a': 'audio/mp4',
      '.flac': 'audio/flac',
      '.txt': 'text/plain',
      '.html': 'text/html',
      '.css': 'text/css',
      '.js': 'text/javascript',
      '.json': 'application/json',
      '.xml': 'application/xml',
      '.zip': 'application/zip',
      '.rar': 'application/vnd.rar',
      '.7z': 'application/x-7z-compressed',
      '.tar': 'application/x-tar',
      '.gz': 'application/gzip',
      '.doc': 'application/msword',
      '.docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      '.xls': 'application/vnd.ms-excel',
      '.xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      '.ppt': 'application/vnd.ms-powerpoint',
      '.pptx': 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      '.rtf': 'application/rtf',
      '.csv': 'text/csv',
    };

    const ext = url?.toLowerCase().match(/\.[a-z0-9]+$/)?.[0];
    return ext ? (mimeTypes[ext] || 'application/octet-stream') : 'application/octet-stream';
  };

  /**
   * Converts MXC URL to HTTP URL
   * @param {string} mxcUrl - Matrix content URL (mxc://...)
   * @returns {string|null} HTTP URL or null if conversion fails
   */
  const convertMxcToHttp = useCallback((mxcUrl) => {
    if (!mxcUrl || !mxcUrl.startsWith('mxc://')) {
      logger.error('Invalid MXC URL:', mxcUrl);
      return null;
    }

    try {
      // Try using the SDK method first with proper parameters
      if (client && typeof client.mxcUrlToHttp === 'function') {
        return client.mxcUrlToHttp(
          mxcUrl,
          undefined,  // width - undefined for full download
          undefined,  // height - undefined for full download
          undefined,  // resizeMethod - undefined for full download
          false,      // allowDirectLinks - false for security
          true,       // allowRedirects - true for authenticated media
          true,        // useAuthentication - true for Matrix 1.11+
        );
      }

      // Fallback: Manual URL construction
      const homeserverUrl = client?.getHomeserverUrl?.() ||
                           localStorage.getItem('matrixHomeserver') ||
                           DEFAULT_HOMESERVER;

      // Parse mxc://serverName/mediaId
      const mxcParts = mxcUrl.replace('mxc://', '').split('/');
      if (mxcParts.length !== 2) {
        logger.error('Invalid MXC URL format:', mxcUrl);
        return null;
      }

      const [serverName, mediaId] = mxcParts;

      // Use v3 endpoint (newer) with fallback to r0 (older)
      const httpUrl = `${homeserverUrl}/_matrix/media/v3/download/${serverName}/${mediaId}`;

      logger.debug('Manually constructed download URL:', httpUrl);
      return httpUrl;
    } catch (error) {
      logger.error('Error converting MXC to HTTP URL:', error);
      return null;
    }
  }, [client]);

  /**
   * Downloads a file with progress tracking using ReadableStream
   * @param {Object} options - Download options
   * @param {string} options.url - Matrix content URL (mxc://)
   * @param {string} options.filename - Desired filename for download
   * @param {string} [options.mimeType] - MIME type of the file
   * @param {string} [options.messageId] - Message ID for progress tracking
   * @returns {Promise<boolean>} Success status
   */
  const downloadFileWithProgress = useCallback(async ({ url, filename, mimeType, messageId }) => {
    if (!url) {
      logger.error('Cannot download: Missing file URL');
      return false;
    }

    const safeFilename = sanitizeFilename(filename);
    const httpUrl = convertMxcToHttp(url);

    if (!httpUrl) {
      logger.error('Failed to generate download URL for file:', safeFilename);
      return false;
    }

    // Create abort controller for cancellation
    const abortController = new AbortController();
    const startTime = Date.now();

    try {
      // Initialize download state if messageId provided
      if (messageId) {
        startDownload(messageId, {
          filename: safeFilename,
          mimeType,
          abortController,
        });
      }

      // Prepare headers with authentication
      const headers = {};
      if (client && typeof client.getAccessToken === 'function') {
        const accessToken = client.getAccessToken();
        if (accessToken) {
          headers.Authorization = `Bearer ${accessToken}`;
        }
      }

      // Fetch with abort signal
      const response = await timFetch(httpUrl, {
        headers,
        signal: abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Download failed: ${response.statusText} (${response.status})`);
      }

      // Get content length for progress calculation
      const contentLength = response.headers.get('Content-Length');
      const totalBytes = contentLength ? parseInt(contentLength, 10) : null;

      logger.debug('Download started:', {
        filename: safeFilename,
        totalBytes,
        hasContentLength: !!totalBytes,
      });

      // Use ReadableStream for progress tracking
      const reader = response.body.getReader();
      const chunks = [];
      let loadedBytes = 0;

      while (true) {
        const { done, value } = await reader.read();

        if (done) break;

        chunks.push(value);
        loadedBytes += value.length;

        // Calculate progress percentage
        const percent = totalBytes
          ? Math.round((loadedBytes / totalBytes) * 100)
          : null;

        // Update progress in context
        if (messageId) {
          updateProgress(messageId, {
            loaded: loadedBytes,
            total: totalBytes,
            percent,
          });

          // Auto-escalate to modal after 5 seconds
          const elapsed = Date.now() - startTime;
          if (elapsed > 5000) {
            escalateToModal(messageId);
          }
        }

        logger.debug('Download progress:', {
          loaded: loadedBytes,
          total: totalBytes,
          percent,
        });
      }

      // Create blob from chunks
      const blob = new Blob(chunks, { type: mimeType || 'application/octet-stream' });
      const blobUrl = URL.createObjectURL(blob);

      // Trigger download
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = safeFilename;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';

      // snyk:ignore:DOM-based Cross-site Scripting (XSS) -- URL is generated from trusted decryption process and sanitized filename
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up blob URL
      setTimeout(() => URL.revokeObjectURL(blobUrl), 100);

      // Complete download
      if (messageId) {
        completeDownload(messageId);
      }

      logger.info('File download completed:', safeFilename);
      return true;

    } catch (error) {
      // Handle cancellation
      if (error.name === 'AbortError') {
        logger.info('Download cancelled:', safeFilename);
        if (messageId) {
          completeDownload(messageId);
        }
        return false;
      }

      // Handle other errors
      logger.error('Error downloading file:', error);
      showError(
        t('FileDownload.downloadFailed'),
        t('FileDownload.downloadError', { filename: safeFilename, error: error.message }),
      );
      if (messageId) {
        completeDownload(messageId);
      }
      return false;
    }
  }, [client, startDownload, updateProgress, completeDownload, escalateToModal, showError, convertMxcToHttp, t]);

  /**
   * Downloads a file attachment from Matrix (legacy method without progress)
   * @param {Object} options - Download options
   * @param {string} options.url - Matrix content URL (mxc://)
   * @param {string} options.filename - Desired filename for download
   * @param {string} [options.mimeType] - MIME type of the file
   * @returns {Promise<boolean>} Success status
   */
  const downloadFile = useCallback(async ({ url, filename, mimeType: _mimeType }) => {
    // Validate client availability
    if (!client) {
      logger.error('Cannot download: Matrix client not available');
      return false;
    }

    if (!url) {
      logger.error('Cannot download: Missing file URL');
      return false;
    }

    const safeFilename = sanitizeFilename(filename);

    try {

      // Convert MXC URL to HTTP URL
      const httpUrl = convertMxcToHttp(url);

      if (!httpUrl) {
        logger.error('Failed to generate download URL for file:', safeFilename);
        return false;
      }

      // Use fetch + blob approach for better cross-origin compatibility
      // and authenticated media support
      const headers = {};

      // Add Authorization header if client has access token
      if (client && typeof client.getAccessToken === 'function') {
        const accessToken = client.getAccessToken();
        if (accessToken) {
          headers.Authorization = `Bearer ${accessToken}`;
        }
      }

      const response = await timFetch(httpUrl, { headers });

      if (!response.ok) {
        throw new Error(`Download failed: ${response.statusText} (${response.status})`);
      }

      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);

      // Create a temporary link element and trigger download
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = safeFilename;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';

      // Add to DOM, click, and remove
      // snyk:ignore:DOM-based Cross-site Scripting (XSS) -- URL is generated from trusted decryption process and sanitized filename
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up blob URL after a brief delay
      setTimeout(() => URL.revokeObjectURL(blobUrl), 100);

      logger.debug('File download initiated:', safeFilename);
      return true;
    } catch (error) {
      logger.error('Error downloading file:', error);
      showError(
        t('FileDownload.downloadFailed'),
        t('FileDownload.downloadError', { filename: safeFilename, error: error.message }),
      );
      return false;
    }
  }, [client, showError, convertMxcToHttp, t]);

  /**
   * Creates a download handler function for use in event handlers
   * @param {Object} options - Download options (same as downloadFile)
   * @returns {Function} Event handler function
   */
  const createDownloadHandler = useCallback((options) => {
    return async (e) => {
      e.preventDefault();
      e.stopPropagation();
      await downloadFile(options);
    };
  }, [downloadFile]);

  /**
   * Downloads a file to a local blob URL for in-app display (e.g., in a modal).
   * This does NOT trigger a download to the user's disk.
   * @param {Object} options - Download options
   * @param {string} options.url - Matrix content URL (mxc://)
   * @param {string} [options.roomId] - Room ID for caching context
   * @param {string} [options.mimeType] - Optional MIME type from Matrix event content (preferred source)
   * @param {Function} [options.onProgress] - Optional callback for download progress updates.
   * @param {AbortSignal} [options.signal] - Optional AbortSignal to cancel the fetch.
   * @param {boolean} [options.skipCache] - Skip cache lookup and storage (for encrypted content)
   * @returns {Promise<string>} A promise that resolves to a local blob URL.
   */
  const downloadFileToDisplay = useCallback(async ({ url, roomId, mimeType, onProgress, signal, skipCache = false }) => {
    if (!url) {
      logger.error('Cannot download for display: Missing file URL');
      throw new Error('Missing file URL');
    }

    // Helper to get effective MIME type: prefer passed mimeType, then infer from URL, fallback to octet-stream
    const getEffectiveMimeType = (serverType) => {
      // Priority: 1) passed mimeType from event content, 2) server Content-Type, 3) infer from URL
      if (mimeType && mimeType !== 'application/octet-stream') return mimeType;
      if (serverType && serverType !== 'application/octet-stream') return serverType;
      return inferMimeType(url);
    };

    // Skip cache for encrypted content (will be cached after decryption)
    if (skipCache) {
      logger.debug('Skipping cache for encrypted content:', url);
    }

    // 1. Check the cache first (unless disabled or skipCache is true)
    if (!cacheDisabled.current && !skipCache) {
      try {
        const cachedBlob = await mediaCacheService.getMedia(url);
        if (cachedBlob) {
          logger.debug('Serving media from IndexedDB cache:', url);
          cacheFailureCount.current = Math.max(0, cacheFailureCount.current - 1); // Decrement on success
          // Ensure cached blob has proper MIME type (for Edge compatibility)
          const effectiveType = getEffectiveMimeType(cachedBlob.type);
          if (!cachedBlob.type || cachedBlob.type === 'application/octet-stream' || cachedBlob.type !== effectiveType) {
            const typedBlob = new Blob([cachedBlob], { type: effectiveType });
            return URL.createObjectURL(typedBlob);
          }
          return URL.createObjectURL(cachedBlob);
        }
      } catch (error) {
        cacheFailureCount.current++;
        logger.warn(`Error checking media cache, (${cacheFailureCount.current}/${CACHE_FAILURE_THRESHOLD}):`, error);
        if (cacheFailureCount.current >= CACHE_FAILURE_THRESHOLD) {
          cacheDisabled.current = true;
          logger.error('Disabling media cache due to repeated failures. Media will be downloaded directly.');
        }
      }
    }

    const httpUrl = convertMxcToHttp(url);

    if (!httpUrl) {
      const err = 'Failed to generate a valid download URL.';
      logger.error(err);
      throw new Error(err);
    }

    try {
      const headers = {};
      if (client && typeof client.getAccessToken === 'function') {
        const accessToken = client.getAccessToken();
        if (accessToken) {
          headers.Authorization = `Bearer ${accessToken}`;
        }
      }

      const response = await timFetch(httpUrl, { headers, signal });

      if (!response.ok) {
        throw new Error(`Download failed: ${response.statusText} (${response.status})`);
      }

      // Get content type from response headers
      const contentType = response.headers.get('Content-Type');

      // Get content length for progress calculation
      const contentLength = response.headers.get('Content-Length');
      const totalBytes = contentLength ? parseInt(contentLength, 10) : null;

      // If no content length or stream, we can't show progress, so download directly.
      if (!totalBytes || !response.body) {
        const blob = await response.blob();
        // Ensure blob has correct MIME type (use passed mimeType, server contentType, or infer for Edge compatibility)
        const effectiveType = getEffectiveMimeType(contentType);
        const typedBlob = new Blob([blob], { type: effectiveType });
        const blobUrl = URL.createObjectURL(typedBlob);
        // Cache unless skipCache is true
        if (!skipCache) {
          try {
            await mediaCacheService.setMedia(url, typedBlob, roomId);
          } catch (cacheError) {
            logger.warn('Failed to cache media in IndexedDB:', cacheError);
          }
        }
        return blobUrl;
      }

      // Use ReadableStream for progress tracking
      const reader = response.body.getReader();
      const chunks = [];
      let loadedBytes = 0;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        chunks.push(value);
        loadedBytes += value.length;

        if (onProgress) {
          const percent = Math.round((loadedBytes / totalBytes) * 100);
          onProgress({ loaded: loadedBytes, total: totalBytes, percent });
        }
      }

      // Create blob with correct MIME type (use passed mimeType, server contentType, or infer for Edge compatibility)
      const effectiveType = getEffectiveMimeType(contentType);
      const blob = new Blob(chunks, { type: effectiveType });
      const blobUrl = URL.createObjectURL(blob);

      // 2. Store the newly downloaded blob in the cache for next time (unless skipCache is true)
      if (!skipCache) {
        try {
          await mediaCacheService.setMedia(url, blob, roomId); // Pass roomId to cache service
          logger.debug('Media cached successfully in IndexedDB:', url);
        } catch (cacheError) {
          logger.warn('Failed to cache media in IndexedDB:', cacheError);
        }
      }

      return blobUrl;
    } catch (error) {
      if (error.name === 'AbortError') {
        logger.debug('Media download cancelled for display:', url);
        throw error;
      }
      logger.error('Error downloading file for display:', { httpUrl, error });
      // Re-throw the error so the calling component can handle it.
      throw error;
    }
  }, [client, convertMxcToHttp]);

  return {
    downloadFile,
    downloadFileWithProgress,
    createDownloadHandler,
    downloadFileToDisplay,
    isAvailable: !!client,
  };
};

export default useFileDownload;